import React, { useEffect, useState } from 'react';
import { PillsInput, Pill, Combobox, CheckIcon, Group, useCombobox, Loader, Menu, Button, ActionIcon } from '@mantine/core';
import { getAllPrimekgNames, getAllPrimekgTypes } from '../../services/primekg';
import { getHardcodedColor } from '../../utils';
import { PiGraphFill } from 'react-icons/pi';
import { IoMdArrowDropdown } from 'react-icons/io';

interface TypeSelectorProps {
    types: string[];
    selectedType: string | null;
    loading: boolean;
    onTypeSelect: (type: string) => void;
}

function TypeSelector({
    types,
    loading,
    selectedType,
    onTypeSelect,
}: TypeSelectorProps) {
    return (
        <Menu position="bottom-end" shadow="md" width={200}>
            <Menu.Target>
                <ActionIcon
                    variant={selectedType ? "filled" : "outline"}
                    size="md"
                    disabled={loading}
                    loading={loading}
                    c={selectedType ? "#fff" : undefined}
                    style={{ backgroundColor: selectedType ? getHardcodedColor(selectedType) : undefined}}
                >
                    {selectedType? <PiGraphFill /> : <IoMdArrowDropdown />}
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                {types.map((type) => (
                    <Menu.Item key={type} onClick={() => onTypeSelect(type)}
                        style={{ backgroundColor: getHardcodedColor(type), color: "#fff", marginBottom: "3px" }}>
                        {type}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
}

interface NodeSearchFieldProps {
    onSelectChange?: (data: string[]) => void;
}

function NodeSearchField({ onSelectChange } : NodeSearchFieldProps) {
    const [data, setData] = useState<string[]>([]);
    const [types, setTypes] = useState<string[]>([]);
    const [loadingTypes, setLoadingTypes] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedType, setSelectedType] = useState<string>(null);
    const [search, setSearch] = useState('');
    const [value, setValue] = useState<string[]>([]);

    useEffect(() => {
        const callGetAllPrimeKgTypes = async () => {
            const names = await getAllPrimekgTypes();
            setTypes(names);
            setLoadingTypes(false)
        };
        setLoadingTypes(true)
        callGetAllPrimeKgTypes()
    }, [])


    useEffect(() => {
        if(!selectedType) return
        
        setValue([]);
        setSearch('');
        setData([]);

        const callGetAllPrimeKgNames = async () => {
            const names = await getAllPrimekgNames(selectedType);
            setData(names)
            setLoading(false)
        };
        setLoading(true)
        callGetAllPrimeKgNames()
    }, [selectedType])

    useEffect(() => {
        onSelectChange(value)
    }, [value])

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const handleValueSelect = (val: string) =>
        setValue((current) =>
            current.includes(val) ? current.filter((v) => v !== val) : [...current, val]
        );

    const handleValueRemove = (val: string) =>
        setValue((current) => current.filter((v) => v !== val));

    const values = value.map((item) => (
        <Pill key={item} withRemoveButton onRemove={() => handleValueRemove(item)}
            style={{ backgroundColor: getHardcodedColor(selectedType), color: "#fff" }}>
            {item}
        </Pill>
    ));

    const options = data
        .filter((item) => item.toLowerCase().includes(search.trim().toLowerCase()))
        .slice(0, 30)
        .map((item) => (
            <Combobox.Option value={item} key={item} active={value.includes(item)}>
                <Group gap="sm">
                    {value.includes(item) ? <CheckIcon size={12} /> : null}
                    <span>{item}</span>
                </Group>
            </Combobox.Option>
        ));

    return (
        <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
            <Combobox.DropdownTarget>
                <PillsInput onClick={() => combobox.openDropdown()}
                    rightSection={
                        <div style={{ width: "100" }}>
                            <TypeSelector
                                loading={loadingTypes || loading}
                                types={types}
                                selectedType={selectedType}
                                onTypeSelect={(type) => {
                                    setSelectedType(type)

                                }} />
                        </div>
                    } style={{ width: "30%" }}>
                    <Pill.Group>
                        {values}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                disabled={!selectedType}
                                value={search}
                                placeholder={selectedType? `Search ${selectedType}` : "Select A Node Type"}
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Backspace' && search.length === 0 && value.length > 0) {
                                        event.preventDefault();
                                        handleValueRemove(value[value.length - 1]);
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown>
                <Combobox.Options style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {options.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
}

export default NodeSearchField;